import type { DurationValueTypes } from 'components/DurationFormat';
import type { DurationObjectUnits } from 'luxon';
import { Duration } from 'luxon';
import { notNull } from 'utils/typesafe';

export type TokenProps = {
  value: number;
  // a duration format following the keys accepted by Luxon's `toFormat`, limited currently to Days - Seconds
  format: string;
  valueType: DurationValueTypes;
  // the type of trimming to apply, e.g. 'right' or true to remove the largest non-zero
  // value or 'both' to trim zero values from both sides
  trim?: 'right' | 'both' | boolean;
  // a key to cease attempting to trim zero values after
  stopTrim?: string;
  trunc?: boolean;
};

const tokenToField = (token: string) => {
  switch (token) {
    case 's':
    case 'ss':
      return 'seconds';
    case 'm':
    case 'mm':
      return 'minutes';
    case 'hh':
    case 'h':
      return 'hours';
    case 'd':
    case 'dd':
      return 'days';
    default:
      return null;
  }
};

const fieldToToken = (field: string) => {
  switch (field) {
    case 'seconds':
      return 's';
    case 'minutes':
      return 'm';
    case 'hours':
      return 'h';
    case 'days':
      return 'd';
    default:
      return null;
  }
};
// takes a string representing a date format, e.g. h'h' mm'm' s's' and figures out
// what is meant as a token and what is meant as a string literal, mostly copied over from Luxon's internals
function parseFormat(fmt: string): Array<{ literal: boolean; val: string }> {
  let current = null,
    currentFull = '',
    escaped = false;
  const splits = [];
  for (let i = 0; i < fmt.length; i++) {
    const c = fmt.charAt(i);
    if (c === "'") {
      if (currentFull.length > 0) {
        splits.push({ literal: escaped, val: currentFull });
      }
      current = null;
      currentFull = '';
      escaped = !escaped;
    } else if (escaped) {
      currentFull += c;
    } else if (c === current) {
      currentFull += c;
    } else {
      if (currentFull.length > 0) {
        splits.push({ literal: false, val: currentFull });
      }
      currentFull = c;
      current = c;
    }
  }

  if (currentFull.length > 0) {
    splits.push({ literal: escaped, val: currentFull });
  }

  return splits;
}

function formatNum(num: number, spaces?: number) {
  if (spaces) {
    const numberAsString = num.toString();
    return numberAsString.padStart(spaces, '0');
  }
  return num;
}
const truncate = (value: number, places: number, trunc: boolean) => {
  const truncMethod = trunc ? Math.floor : Math.round;
  const factor = 10 ** places;

  return truncMethod(value * factor) / factor;
};

interface ShouldBreakParams {
  stopTrimKey: string | null;
  currentKeyMagnitude: number;
  largestNonZeroKeyMagnitude: number;
  isLargestNonZeroKey: boolean;
  trimLargest: boolean;
}

const shouldBreakFormatLoop = ({
  stopTrimKey,
  currentKeyMagnitude,
  largestNonZeroKeyMagnitude,
  isLargestNonZeroKey,
  trimLargest,
}: ShouldBreakParams) => {
  if (stopTrimKey) {
    const trimKeyMagnitude = types.indexOf(stopTrimKey);

    if (currentKeyMagnitude > trimKeyMagnitude) {
      return false;
    } else if (
      currentKeyMagnitude <= trimKeyMagnitude &&
      trimKeyMagnitude <= largestNonZeroKeyMagnitude
    ) {
      return true;
    }
  }
  return isLargestNonZeroKey && trimLargest;
};

// Token type names in order of descending magnitude.
const types = 'days hours minutes seconds'.split(' ');

export function customDurationFormat(props: TokenProps) {
  const { value, format, valueType, trim, stopTrim, trunc = true } = props;

  // an array of objects representing the tokens we passed on, separating out suffixes from values
  const parsedTokens = parseFormat(format);

  // figure out what keys we want to represent our duration as
  const requiredKeys = parsedTokens
    .map(token => {
      if (token.literal || token.val === ' ') {
        return null;
      }
      return tokenToField(token.val);
    })
    .filter(notNull);

  const durationDt = Duration.fromObject({ [valueType]: value }).shiftTo(...requiredKeys);

  if (!trim) {
    const durationObject = durationDt.toObject();
    for (const key in durationObject) {
      const k = key as keyof DurationObjectUnits;
      durationObject[k] = truncate(durationDt.get(k), 0, trunc);
    }
    return Duration.fromObject(durationObject).toFormat(format);
  }

  const reduceFn = trim === 'both' ? 'reduce' : 'reduceRight';

  // an object representing the available keys and their current values, along with spaces
  // e.g. { hours: 0, space1: null, minutes: 50, space2: null, seconds: 0 }
  const unitKeys = parsedTokens[reduceFn](
    (previous: Record<string, number | string | null>, parsedToken, idx) => {
      if (!parsedToken.literal) {
        const token = tokenToField(parsedToken.val);
        if (parsedToken.val === ' ') {
          return {
            ...previous,
            ['space' + idx.toString()]: null,
          };
        }
        if (token) {
          const tokenValue = truncate(durationDt.get(token as keyof DurationObjectUnits), 0, trunc);
          return {
            ...previous,
            [token]:
              parsedToken.val.length >= 2
                ? formatNum(tokenValue, parsedToken.val.length)
                : tokenValue,
          };
        }
        return previous;
      }
      return previous;
    },
    {}
  );

  let formatString = '';
  let largestNonZeroKeyMagnitude = 100;

  Object.keys(unitKeys).forEach(key => {
    const keyMagnitude = types.indexOf(key);
    const value = unitKeys[key];

    if (keyMagnitude >= 0 && value && +value > 0 && keyMagnitude < largestNonZeroKeyMagnitude) {
      largestNonZeroKeyMagnitude = keyMagnitude;
    }
  });

  if (unitKeys) {
    for (const key in unitKeys) {
      const currentKeyMagnitude = types.indexOf(key);
      const isLargestNonZeroKey = currentKeyMagnitude === largestNonZeroKeyMagnitude;
      const stopTrimKey = stopTrim ? tokenToField(stopTrim) : null;
      const value = +unitKeys[key]!;
      const trimLargest = trim === 'right' || trim === true;

      if (unitKeys[key] == null) {
        formatString = trimLargest ? ' ' + formatString : formatString + ' ';
        continue;
      }

      const token = fieldToToken(key);
      const tokenIndex = token
        ? parsedTokens.findIndex(t => !t.literal && t.val.includes(token))
        : -1;
      const suffixIndex = tokenIndex + 1;

      if (trim === 'both' && value !== 0) {
        formatString = formatString + unitKeys[key] + parsedTokens[suffixIndex]?.val;
      }

      if (trimLargest) {
        formatString = unitKeys[key] + parsedTokens[suffixIndex]?.val + formatString;
      }

      const shouldBreak = shouldBreakFormatLoop({
        currentKeyMagnitude,
        isLargestNonZeroKey,
        largestNonZeroKeyMagnitude,
        stopTrimKey,
        trimLargest,
      });

      if (shouldBreak) break;
    }
  }

  return formatString.trim();
}
